import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getToken } from '@/core/token';
import { execute } from '@/graphql/execute';
import { deletePasskeyMutation, userPasskeysQuery } from '@/queries/me.query';
import { startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Loader2, Trash2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const ENDPOINT = import.meta.env.VITE_ENDPOINT;

export const PasskeySettings: FunctionComponent = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('account');
  const [isRegistering, setIsRegistering] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['userPasskeys'],
    queryFn: () => execute(userPasskeysQuery),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => execute(deletePasskeyMutation, { id }),
    onSuccess: () => {
      analytics.capture('Passkey Deleted');
      queryClient.invalidateQueries({ queryKey: ['userPasskeys'] });
      toast.success(t('passkeys.deleted'));
    },
    onError: () => toast.error(t('passkeys.failedDelete')),
  });

  const registerPasskey = async () => {
    setIsRegistering(true);
    const toastId = toast.loading(t('passkeys.settingUp'));
    try {
      const token = getToken();
      if (!token) throw new Error('Not authenticated');

      // Get registration options
      const optionsRes = await fetch(`${ENDPOINT}/passkey/register-options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const { options } = await optionsRes.json();

      // Start WebAuthn registration
      const credential = await startRegistration({ optionsJSON: options });

      // Verify registration
      const verifyRes = await fetch(`${ENDPOINT}/passkey/register-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          credential,
          challenge: options.challenge,
        }),
      });

      const result = await verifyRes.json();
      if (!result.success) throw new Error(result.error);

      analytics.capture('Passkey Registered');
      queryClient.invalidateQueries({ queryKey: ['userPasskeys'] });
      toast.success(t('passkeys.registeredSuccess'), { id: toastId });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        toast.error(t('passkeys.registrationCancelled'), { id: toastId });
      } else {
        toast.error(error.message || t('passkeys.failedRegister'), { id: toastId });
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const passkeys = data?.userPasskeys ?? [];

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle>{t('passkeys.title')}</CardTitle>
            <CardDescription>{t('passkeys.description')}</CardDescription>
          </div>
          <Button onClick={registerPasskey} disabled={isRegistering}>
            {isRegistering ? <Loader2 className='size-4 animate-spin mr-2' /> : <Fingerprint className='size-4 mr-2' />}
            {t('passkeys.addPasskey')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex justify-center py-4'>
            <Loader2 className='size-5 animate-spin' />
          </div>
        ) : passkeys.length === 0 ? (
          <p className='text-sm text-muted-foreground py-2'>
            {t('passkeys.noPasskeys')}
          </p>
        ) : (
          <div className='space-y-3'>
            {passkeys.map((pk) => (
              <div key={pk.id} className='flex items-center justify-between p-3 border rounded-lg'>
                <div className='flex items-center gap-3'>
                  <Fingerprint className='size-5 text-muted-foreground' />
                  <div>
                    <p className='text-sm font-medium'>{pk.name}</p>
                    <p className='text-xs text-muted-foreground'>
                      {t('passkeys.added', { date: new Date(pk.createdAt).toLocaleDateString() })}
                      {pk.lastUsedAt && ` · ${t('passkeys.lastUsed', { date: new Date(pk.lastUsedAt).toLocaleDateString() })}`}
                    </p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant='ghost' size='icon' disabled={deleteMutation.isPending}>
                      <Trash2 className='size-4 text-destructive' />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('passkeys.deleteTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('passkeys.deleteDescription', { name: pk.name })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('passkeys.cancel')}</AlertDialogCancel>
                      <AlertDialogAction variant='destructive' onClick={() => deleteMutation.mutate(pk.id)}>
                        {t('passkeys.delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
